import { ErrorMessage, type InjectedFormikProps, withFormik } from "formik";
import Fuse from "fuse.js";
import { uniq } from "lodash-es";
import moment from "moment";
import { useContext, useMemo } from "react";

import { BTButton, BTPopover, BTStatusBadge } from "@btnet/core-components";

import { BdsIconPaperPlaneRight, BdsIconWarning } from "@buildertrend/components";

import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { type BTSelectItem } from "types/apiResponse/apiResponse";
import { BTLoginTypes, type DiscussionsLinkedTypes } from "types/enum";

import { getValidateStatusTouched } from "utilities/form/form";
import yup from "utilities/form/yup";
import { deepClone } from "utilities/object/object";
import { type IWebviewRouteParams } from "utilities/routes";

import { BTForm, BTFormItem } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTEditorChat } from "commonComponents/btWrappers/editor/BTEditorChat/BTEditorChat";
import { isUnsupportedEntity } from "commonComponents/btWrappers/editor/BTEditorChat/BTEditorChatInterface";
import {
    MentionUserTypes,
    type UserMentionSuggestion,
} from "commonComponents/btWrappers/editor/core/extensions/custom/mentionUser/MentionUserExtension";
import { getDefaultJSONComment } from "commonComponents/entity/comment/CommentContainer/CommentContainer";
import {
    type CommentFormActions,
    type CommentPermissions,
    type DiscussionCommentEntity,
    type ICommentFormValues,
    type ICommentInfo,
    type IMentionableUser,
} from "commonComponents/entity/comment/CommentContainer/CommentContainer.api.types";
import {
    type IHasContent,
    scanForMentions,
} from "commonComponents/entity/comment/CommentContainer/CommentContainer.utils";
import { CommentParticipantsList } from "commonComponents/entity/comment/CommentContainer/CommentParticipants";
import {
    ParticipantData,
    type ParticipantsResponse,
} from "commonComponents/entity/discussion/DiscussionContainer/DiscussionContainer.api.types";

import { useChatEditorConfig } from "entity/chat/common/useChatEditorConfig";

interface IAddNewCommentProps {
    addNewCommentPermissions: CommentPermissions;
    comment: ICommentInfo;
    defaultNotifyUsers: number[];
    linkedId: number;
    notificationUsers: BTSelectItem[];
    mentionableUsers: IMentionableUser[];
    linkedType?: DiscussionsLinkedTypes;
    filterSubsAndOwnerNotifications: (
        formValues: ICommentFormValues,
        notificationUsers: BTSelectItem<undefined>[],
    ) => ICommentFormValues;
    closeOnSend?: boolean;
    actionBeingPerformed: CommentFormActions;
    onAddComment: (
        linkedId: number,
        newComment: ICommentFormValues,
        action: CommentFormActions,
    ) => Promise<boolean>;
    reloadData: () => Promise<void>;
    onClose?: () => void;
    webviewConfig?: IWebviewRouteParams;
    jobId: number;
    addReplyToListener: (
        name: string,
        listener: (comment: DiscussionCommentEntity) => void,
    ) => void;
    additionalFooterContent?: React.ReactNode;
    participants?: ParticipantsResponse;
}

function AddNewComment({
    values,
    touched,
    errors,
    setFieldValue,
    setFieldTouched,
    mentionableUsers,
    isSubmitting,
    linkedType,
    webviewConfig,
    onClose,
    handleSubmit,
    setSubmitting,
    validateForm,
    onAddComment,
    linkedId,
    reloadData,
    resetForm,
    closeOnSend,
    actionBeingPerformed,
    jobId,
    addReplyToListener,
    additionalFooterContent,
    isValid,
    participants,
}: InjectedFormikProps<IAddNewCommentProps, ICommentFormValues>) {
    const userInfo = useContext(UserInfoContext);

    const hasFormErrors = !isValid || Object.keys(errors).length > 0;

    const getTooltipText = () => {
        if (hasFormErrors) {
            const errorMessages = Object.values(errors).filter(Boolean);
            if (errorMessages.length > 0) {
                return errorMessages.join(", ");
            }
            return "Add content to send";
        }

        return "Send (ctrl + enter)";
    };

    const customHandleSubmit = async (
        e: React.MouseEvent<HTMLFormElement, MouseEvent> | undefined,
        action: CommentFormActions,
    ) => {
        let saveSuccessful = false;

        // call normal formik handleSubmit (formik handleSubmit sets submit count, touched, etc)
        handleSubmit(e);

        setSubmitting(true);

        const formErrors = await validateForm(values);
        if (Object.keys(formErrors).length === 0) {
            saveSuccessful = await onAddComment(linkedId, values, action);

            const formReset = { ...values };
            formReset.commentJson = getDefaultJSONComment(true);

            resetForm(formReset);
        }
        setSubmitting(false);

        if (e) {
            e.stopPropagation();
        }

        if (action === "sendAndClose" && saveSuccessful) {
            onClose?.();
        }
        if (saveSuccessful) {
            await reloadData();
        }
    };

    const editorConfig = useChatEditorConfig({
        jobId,
        searchUsers: async (query: string) => {
            const cleanQuery = query.toLowerCase();

            const searchableUsers: UserMentionSuggestion[] = (mentionableUsers ?? []).map((u) => ({
                globalUserId: u.id,
                mentionUserType: MentionUserTypes.User,
                name: u.name,
            }));

            if (userInfo?.loginType === BTLoginTypes.BUILDER) {
                searchableUsers.unshift({
                    mentionUserType: MentionUserTypes.Everyone,
                    count: searchableUsers.length, // Count not including the @everyone mention
                });
            }

            if (cleanQuery.length === 0) {
                return searchableUsers.slice(0, 5);
            }

            // Transform data for Fuse.js search
            const searchableData = searchableUsers.map((user) => ({
                ...user,
                searchName:
                    user.mentionUserType === MentionUserTypes.Everyone ? "Everyone" : user.name,
            }));

            const fuseInstance = new Fuse(searchableData, {
                keys: ["searchName"],
                threshold: 0.3,
                ignoreLocation: true, // Makes last name search the same score as first name search
                includeScore: true,
            });

            return fuseInstance
                .search(cleanQuery)
                .map((r) => r.item)
                .slice(0, 5);
        },
        disableMentionUsers: false,
        unsupportedMentionUserEntityType: isUnsupportedEntity(linkedType),
        // Invoices are already covered under ownerPayments here
    });

    const handleOnSendChat = async () => {
        if (closeOnSend) {
            await customHandleSubmit(undefined, "sendAndClose");
        } else {
            await customHandleSubmit(undefined, "send");
        }
    };

    addReplyToListener("replyContent", (replyingToComment) => {
        const editorContent = deepClone(values.commentJson!);
        const replyCommentJson = deepClone(replyingToComment.commentJson);
        setFieldValue("commentJson", {
            ...editorContent,
            replyContent: {
                ...replyCommentJson,
                content: replyingToComment.commentText,
                userFullName: replyingToComment.userFullName,
                commentDate: moment(replyingToComment.commentDate),
                commentDateUtc: replyingToComment.commentDateUtc
                    ? moment(replyingToComment.commentDateUtc)
                    : undefined,
                replyContent: undefined,
            },
        });
    });

    const { globalUserId } = useRequiredContext(UserInfoContext);

    const activeParticipants = useMemo(() => {
        return [
            ...(participants?.internalParticipants.filter((p) => p.canNotify) ?? []),
            ...(participants?.subParticipants.filter((p) => p.canNotify) ?? []),
            ...(participants?.clientParticipants.filter((p) => p.canNotify) ?? []),
        ];
    }, [participants]);

    const activeMentions = useMemo(() => {
        const mentionedIds = scanForMentions(
            mentionableUsers,
            values.commentJson as unknown as IHasContent,
            [],
        );
        return uniq(
            mentionedIds.filter(
                (id) => id !== globalUserId && !activeParticipants.some((u) => u.userId === id),
            ),
        ).map((id) => {
            const mentionData = mentionableUsers.find((u) => u.id === id);
            return new ParticipantData({
                userId: mentionData?.id ?? 0,
                name: mentionData?.name ?? "",
                canNotify: true,
            });
        });
    }, [activeParticipants, mentionableUsers, values.commentJson, globalUserId]);

    let totalParticipantCount = 0;
    if (participants) {
        totalParticipantCount = activeParticipants.length;
    }
    totalParticipantCount += activeMentions.length;

    const warnBadge = totalParticipantCount >= 20;
    const isSending = actionBeingPerformed === "send" || actionBeingPerformed === "sendAndClose";

    return (
        <BTForm
            className="CommentJsonContainer HasInvertedLayout"
            data-testid="add-new-comment-form"
        >
            <BTFormItem
                validateStatus={getValidateStatusTouched(errors, touched, "commentJson")}
                help={<ErrorMessage name="commentJson" />}
            >
                <BTEditorChat<ICommentFormValues, CommentFormActions>
                    id="commentJson"
                    data-testid="commentJson"
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    value={values.commentJson!}
                    readOnly={isSending}
                    onSendChat={handleOnSendChat}
                    loadingAction="send"
                    actionBeingPerformed={actionBeingPerformed}
                    isWebView={!!webviewConfig}
                    disableSubmitOnEnterKey
                    extensionConfig={editorConfig}
                    onClearReplyContent={() => {
                        // If the user is at the start of the editor, delete the reply content
                        setFieldValue("commentJson", {
                            ...values.commentJson,
                            replyContent: undefined,
                        });
                    }}
                    addReplyToListener={(comment) => addReplyToListener("mentionContent", comment)}
                />
            </BTFormItem>
            {!isSubmitting && (
                <div className="BdsEditorChatControlBar-Lower">
                    {!hasFormErrors && (
                        <BTPopover
                            data-testid="comment-participants"
                            disabled={totalParticipantCount <= 0}
                            overlayClassName="CommentParticipantsTooltip"
                            overlayStyle={{ zIndex: 50, background: "white" }}
                            widthType="unrestricted"
                            content={
                                <CommentParticipantsList
                                    participants={[...activeParticipants, ...activeMentions]}
                                />
                            }
                        >
                            <div className="margin-right-xs">
                                <BTStatusBadge
                                    displayType={warnBadge ? "warning" : undefined}
                                    icon={warnBadge ? <BdsIconWarning /> : undefined}
                                    textOnly={!warnBadge}
                                    testid="participant-count"
                                    text={`${totalParticipantCount} will be notified`}
                                />
                            </div>
                        </BTPopover>
                    )}
                    {additionalFooterContent}
                    <BTPopover content={getTooltipText()}>
                        <BTButton
                            id="sendChat"
                            data-testid="sendChat"
                            type="primary"
                            icon={<BdsIconPaperPlaneRight />}
                            onClick={handleOnSendChat}
                            loading={actionBeingPerformed === "send"}
                            disabled={hasFormErrors}
                        >
                            Send
                        </BTButton>
                    </BTPopover>
                </div>
            )}
        </BTForm>
    );
}

const FormValuesValidator = () =>
    yup.object().shape<ICommentFormValues>({
        commentJson: yup
            .mixed()
            .editorContentValid({
                maxContentSize: 25000,
                requireContent: true,
                hasBase64Images: false,
                hasUploadedImages: false,
            })
            .label("Comment Text"),
        linkedType: yup.number().required().label("Linked Type"),
        notificationUsers: yup.array<number>().notRequired().label("Notify Users"),
        mentionableUsers: yup.array<number>().notRequired().label("Mentionable Users"),
        showOwner: yup.boolean().notRequired().label("Show Client"),
        showSubs: yup.boolean().notRequired().label("Show Subs"),
    });

export const AddNewCommentForm = withFormik<IAddNewCommentProps, ICommentFormValues>({
    // use the default values we specified above
    mapPropsToValues: (props: IAddNewCommentProps): ICommentFormValues => ({
        commentJson: props.comment.commentJson,
        linkedType: props.comment.linkedType,
        notificationUsers: props.defaultNotifyUsers,
        mentionableUsers: props.comment.mentionableUsers.map((user) => user.id),
        showOwner: props.comment.showOwner,
        showSubs: props.comment.showSubs,
        channelId: props.comment.channelId,
    }),

    // use the yup validation object we specified above
    validationSchema: FormValuesValidator,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,

    // use the submit function we specified above
    handleSubmit: () => {},
})(AddNewComment);
